import { bookingQuerySchema } from 'v5/api/booking/booking.api';
import { calloutsSearchQuerySchema } from 'v5/api/callouts/callouts.api';
import { callsheetSearchQuerySchema } from 'v5/api/callsheet/callsheet.api';
import { productionSearchQuerySchema } from 'v5/api/production/production.api';
import { reconciliationQuerySchema } from 'v5/api/reconciliation/reconciliation.api';
import { resourceQuerySchema } from 'v5/api/resource/resource.api';
import { shortlistQuerySchema } from 'v5/api/shortlist/shortlist.api';
import { appRouting } from 'v5/core/routing/appRouting';
import { myRouting } from 'v5/features/core/routing';
import { resourceTypeAdminListQuerySchema } from 'v5/features/resource-types-admin/resourceTypeAdminListQuery';
import {
  ListQueryDefinition,
  toLocationParseQueryParams
} from './listQueryDefinition';

const legacy = myRouting.forWorkspace();
const enterpriseWs = appRouting.org().workspace();
const enterpriseTeam = appRouting.org().workspace().team();
const settingsGlobal = appRouting.org().settings.global;
const settingsWorkspace = appRouting.org().settings.workspace();

/**
 * Single source of truth for main-app list URL query contracts:
 * which LocationService entity key, Zod schema, and route patterns (from routing)
 * participate in parse + persistence.
 *
 * Route path strings come only from myRouting / appRouting.
 */
export const appListQueries = {
  productions: {
    listId: 'productions',
    schema: productionSearchQuerySchema,
    routes: [
      {
        pattern: legacy.productions().type(),
        browsingScope: 'organization',
        subtypeParam: 'productionTypeId'
      },
      {
        pattern: legacy.productions().root(),
        browsingScope: 'organization'
      },
      {
        pattern: enterpriseTeam.productions.type(),
        browsingScope: 'team',
        subtypeParam: 'productionTypeId'
      },
      {
        pattern: enterpriseTeam.productions.root(),
        browsingScope: 'team'
      }
    ]
  },
  callsheets: {
    listId: 'callsheets',
    schema: callsheetSearchQuerySchema,
    routes: [
      {
        pattern: legacy.callsheets().root(),
        browsingScope: 'organization'
      },
      {
        pattern: enterpriseTeam.callsheets.root(),
        browsingScope: 'team'
      }
    ]
  },
  resources: {
    listId: 'resources',
    schema: resourceQuerySchema,
    routes: [
      {
        pattern: legacy.resources().item(),
        browsingScope: 'organization',
        subtypeParam: 'resourceTypeId'
      },
      {
        pattern: legacy.resources().root(),
        browsingScope: 'organization'
      },
      {
        pattern: enterpriseWs.resources.forResourceType(),
        browsingScope: 'workspace',
        subtypeParam: 'resourceTypeId'
      },
      {
        pattern: enterpriseWs.resources.root(),
        browsingScope: 'workspace'
      }
    ]
  },
  organizationTalents: {
    listId: 'organization-talents',
    schema: resourceQuerySchema,
    routes: [
      {
        pattern: legacy.organizationTalents().root(),
        browsingScope: 'organization'
      }
    ]
  },
  shortlists: {
    listId: 'shortlists',
    schema: shortlistQuerySchema,
    routes: [
      {
        pattern: legacy.shortlists().root(),
        browsingScope: 'organization'
      },
      {
        pattern: enterpriseTeam.shortlists.root(),
        browsingScope: 'team'
      }
    ]
  },
  callouts: {
    listId: 'callouts',
    schema: calloutsSearchQuerySchema,
    routes: [
      {
        pattern: legacy.callouts().root(),
        browsingScope: 'organization'
      },
      {
        pattern: enterpriseWs.callouts.root(),
        browsingScope: 'workspace'
      }
    ]
  },
  bookings: {
    listId: 'bookings',
    schema: bookingQuerySchema,
    routes: [
      {
        pattern: legacy.bookings().type(),
        browsingScope: 'organization',
        subtypeParam: 'bookingTypeId'
      },
      {
        pattern: legacy.bookings().root(),
        browsingScope: 'organization'
      },
      {
        pattern: enterpriseWs.bookings.type(),
        browsingScope: 'workspace',
        subtypeParam: 'bookingTypeId'
      },
      {
        pattern: enterpriseWs.bookings.root(),
        browsingScope: 'workspace'
      }
    ]
  },
  reconciliations: {
    listId: 'reconciliations',
    schema: reconciliationQuerySchema,
    routes: [
      {
        pattern: legacy.reconciliations().root(),
        browsingScope: 'organization'
      }
    ]
  },
  resourceTypesAdmin: {
    listId: 'resource-types-admin',
    schema: resourceTypeAdminListQuerySchema,
    routes: [
      {
        pattern: settingsGlobal.resourceTypes(),
        browsingScope: 'organization'
      },
      {
        pattern: settingsWorkspace.resourceTypes(),
        browsingScope: 'workspace'
      }
    ]
  },
  /** Fields All/Archived on resource-type detail (query, not /fields/archived). */
  resourceTypeFieldsAdmin: {
    listId: 'resource-type-fields-admin',
    schema: resourceTypeAdminListQuerySchema,
    routes: [
      {
        pattern: settingsGlobal.resourceTypeTab(),
        browsingScope: 'organization',
        subtypeParam: 'resourceTypeId'
      },
      {
        pattern: settingsWorkspace.resourceType(),
        browsingScope: 'workspace',
        subtypeParam: 'resourceTypeId'
      }
    ]
  }
} as const satisfies Record<string, ListQueryDefinition>;

export const appLocationParseQueryParams =
  toLocationParseQueryParams(appListQueries);

export type AppListQueryParams = {
  [K in keyof typeof appListQueries]: ReturnType<
    (typeof appListQueries)[K]['schema']['parse']
  >;
};
